import styled from '@emotion/styled';
import * as React from 'react';
import { useCallback } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../../../components/button';
import { FormGroup } from '../../../../../../../components/form/FormGroup';
import InputError from '../../../../../../../components/form/InputError';
import { SelectDropdown } from '../../../../../../../components/Select/SelectDropdown';
import { GoalKeyResult } from '../types';
import { GoalFormProvider, scrollToFirstError } from '../goalFormState';
import {
  CreateTimeEntryResultData,
  useCreateKeyResultForm,
} from './useCreateKeyResultForm';

type Props = {
  show: boolean;
  onClose: () => void;
  onSubmit: (keyResult: GoalKeyResult) => void;
};

const i18prefix = 'performanceReview.goals2.keyResults';

export const CreateKeyResultModal: React.FC<React.PropsWithChildren<Props>> = ({
  show,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const {
    form,
    startValueField,
    targetValueField,
    metricValueField,
    titleField,
  } = useCreateKeyResultForm();

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const errors = form.validate();
      if (errors) {
        scrollToFirstError(errors);
        return;
      }
      onSubmit(mapKeyResult(form.values));
    },
    [form, onSubmit],
  );

  const { metricValue, startValue } = form.values;

  const metricOptions = [
    { label: t(`${i18prefix}.types.percentage`), value: 'PERCENTAGE' },
    { label: t(`${i18prefix}.types.numeric`), value: 'NUMERIC' },
    { label: t(`${i18prefix}.types.boolean`), value: 'BOOLEAN' },
  ];

  const isNumericOrPercentage = () => {
    return ['PERCENTAGE', 'NUMERIC'].includes(metricValue);
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton className="card-header">
        <h4 className="card-header-title">{t(`${i18prefix}.addKeyResult`)}</h4>
      </Modal.Header>
      <GoalFormProvider form={form}>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            <div data-field={titleField}>
              <FormGroup>
                <Form.Label>{t(`${i18prefix}.title`)}</Form.Label>
                <Form.Control
                  autoFocus
                  name={titleField}
                  value={form.values.title}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    form.setFieldValue('title', e.target.value)
                  }
                  isInvalid={!!form.errors.title}
                />
                {form.errors.title && <InputError error={form.errors.title} />}
              </FormGroup>
            </div>
            <ValuesContainer>
              <FormGroup data-field={metricValueField}>
                <Form.Label>{t(`${i18prefix}.metric`)}</Form.Label>
                <SelectDropdown
                  options={metricOptions}
                  value={form.values.metricValue}
                  onChange={(val) => {
                    if (val) {
                      form.setFieldValue(
                        'metricValue',
                        val as CreateTimeEntryResultData['metricValue'],
                      );
                    }
                  }}
                  heightMode="large"
                />
              </FormGroup>
              {isNumericOrPercentage() && (
                <>
                  <FormGroup data-field={startValueField}>
                    <Form.Label>{t(`${i18prefix}.start`)}</Form.Label>
                    <Form.Control
                      name={startValueField}
                      value={form.values.startValue}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        form.setFieldValue(
                          'startValue',
                          e.target.value === ''
                            ? ('' as unknown as number)
                            : Number(e.target.value),
                        )
                      }
                      isInvalid={!!form.errors.startValue}
                    />
                    {form.errors.startValue && (
                      <InputError error={form.errors.startValue} />
                    )}
                  </FormGroup>
                  <FormGroup data-field={targetValueField}>
                    <Form.Label>{t(`${i18prefix}.target`)}</Form.Label>
                    <Form.Control
                      name={targetValueField}
                      value={form.values.targetValue ?? ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        form.setFieldValue(
                          'targetValue',
                          e.target.value === '' ? null : Number(e.target.value),
                        )
                      }
                      isInvalid={!!form.errors.targetValue}
                    />
                    {form.errors.targetValue && (
                      <InputError error={form.errors.targetValue} />
                    )}
                  </FormGroup>
                </>
              )}
              {metricValue === 'BOOLEAN' && (
                <>
                  <FormGroup data-field={startValueField}>
                    <Form.Label>{t(`${i18prefix}.start`)}</Form.Label>
                    <SelectDropdown
                      options={[
                        { value: '0', label: t(`${i18prefix}.no`) },
                        { value: '100', label: t(`${i18prefix}.yes`) },
                      ]}
                      value={String(form.values.startValue)}
                      onChange={(val) => {
                        form.setFieldValue(
                          'startValue',
                          val ? parseInt(val) : 0,
                        );
                      }}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Form.Label>{t(`${i18prefix}.target`)}</Form.Label>
                    <p className="form-control-plaintext">
                      {startValue === 0 ? 'Yes' : 'No'}
                    </p>
                  </FormGroup>
                </>
              )}
            </ValuesContainer>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={onClose}
              label={t(`form.general.cancelButton`)}
            />

            <Button
              type="submit"
              isProcessing={form.isSubmitting}
              label={t('form.general.saveButton')}
            />
          </Modal.Footer>
        </form>
      </GoalFormProvider>
    </Modal>
  );
};

const ValuesContainer = styled('div')`
  display: grid;
  grid-template-columns: 3fr 1fr 1fr 2fr;
  gap: 10px;
`;

const mapKeyResult = (src: CreateTimeEntryResultData): GoalKeyResult => {
  switch (src.metricValue) {
    case 'NUMERIC':
    case 'PERCENTAGE':
    case 'BOOLEAN':
      return {
        name: src.title,
        type: src.metricValue,
        initialValue: src.startValue ?? 0,
        currentValue: src.startValue ?? 0,
        targetValue: src.targetValue ?? 100,
      };
  }
};
